import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { TemplateVariable } from '@/types/notificationTemplate.types';

interface VariablesPanelProps {
  variables: TemplateVariable[];
}

export function VariablesPanel({ variables }: VariablesPanelProps) {
  const { t } = useTranslation();

  const handleCopy = async (variableName: string) => {
    await navigator.clipboard.writeText(`{{${variableName}}}`);
    toast.success(t('notificationTemplates.toasts.variableCopied'));
  };

  if (variables.length === 0) {
    return null;
  }

  return (
    <div>
      <h4 className='text-sm font-medium text-gray-700 mb-1'>{t('notificationTemplates.editor.variables')}</h4>
      <p className='text-xs text-gray-500 mb-2'>{t('notificationTemplates.editor.variablesCopy')}</p>
      <div className='overflow-hidden rounded border border-gray-200'>
        <table className='min-w-full divide-y divide-gray-200 text-xs'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-3 py-2 text-left font-medium text-gray-500'>
                {t('notificationTemplates.editor.variableName')}
              </th>
              <th className='px-3 py-2 text-left font-medium text-gray-500'>
                {t('notificationTemplates.editor.variableDescription')}
              </th>
              <th className='px-3 py-2 text-left font-medium text-gray-500'>
                {t('notificationTemplates.editor.variableSample')}
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {variables.map((v) => (
              <tr
                key={v.name}
                onClick={() => void handleCopy(v.name)}
                className='cursor-pointer hover:bg-blue-50 transition-colors'>
                <td className='px-3 py-2 font-mono text-blue-700 whitespace-nowrap'>
                  <span className='inline-flex items-center gap-1'>
                    <Copy className='h-3 w-3 text-gray-400' />
                    {`{{${v.name}}}`}
                  </span>
                </td>
                <td className='px-3 py-2 text-gray-600'>{v.description}</td>
                <td className='px-3 py-2 text-gray-500 italic'>{v.sampleValue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
